import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { fmtDate, today } from '../lib/format';
import { MONTH_NAMES, WEEKDAYS, addDaysIso, addMonthsIso, isoOf, longDate, monthGrid, parseDateText, partsOf } from '../lib/dates';
import { Icon } from './kit';

/** The change it reports has the same shape as an input's change event, so it replaces <input type="date"> one for one. */
type DateChange = { target: { value: string } };

export interface DateInputProps {
  /** YYYY-MM-DD, or '' for none. */
  value: string;
  onChange: (e: DateChange) => void;
  id?: string;
  className?: string;
  min?: string | undefined;
  max?: string | undefined;
  disabled?: boolean;
  autoFocus?: boolean;
  onKeyDown?: (e: ReactKeyboardEvent<HTMLInputElement>) => void;
  'aria-describedby'?: string | undefined;
  'aria-invalid'?: boolean | undefined;
  'aria-label'?: string | undefined;
  /** Shown while empty. Never a format mask like MM/DD/YYYY: dates read "Oct 31, 2026" everywhere (APP-DESIGN.md 9.7). */
  placeholder?: string;
}

const outOfRange = (iso: string, min?: string, max?: string) => (!!min && iso < min) || (!!max && iso > max);
const clamp = (iso: string, min?: string, max?: string) => (min && iso < min ? min : max && iso > max ? max : iso);

/**
 * A date field: type a date (10/17/2026, Oct 17 2026, 2026-10-17) or pick it from a calendar that
 * opens on a click or with the down arrow. The calendar is large enough to hit comfortably, moves
 * by month or year, and works from the keyboard (arrows, Page Up/Down, Home/End, Enter, Escape).
 */
export function DateInput({ value, onChange, id, className = 'input', min, max, disabled, autoFocus, onKeyDown, placeholder = 'Choose a date', ...aria }: DateInputProps) {
  const [text, setText] = useState(value ? fmtDate(value) : '');
  const [editing, setEditing] = useState(false);
  const [open, setOpen] = useState(false);
  const [byKey, setByKey] = useState(false);
  const field = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);

  // Show the value as a readable date, except while it is being typed.
  useEffect(() => { if (!editing) setText(value ? fmtDate(value) : ''); }, [value, editing]);

  const emit = (iso: string) => { if (iso !== value) onChange({ target: { value: iso } }); };
  /** Take what was typed: a date becomes the value, nothing clears it, anything else goes back to the value. */
  const commit = () => {
    setEditing(false);
    const typed = text.trim();
    if (!typed) { emit(''); return; }
    const iso = parseDateText(typed);
    if (iso && !outOfRange(iso, min, max)) emit(iso);
    else setText(value ? fmtDate(value) : '');
  };
  const pick = (iso: string) => {
    emit(iso);
    setText(fmtDate(iso));
    setEditing(false);
    setOpen(false);
    input.current?.focus();
  };

  return (
    <div className={`date-field ${disabled ? 'is-disabled' : ''}`} ref={field}>
      <input
        ref={input}
        id={id}
        className={className}
        value={text}
        disabled={disabled}
        autoFocus={autoFocus}
        inputMode="numeric"
        autoComplete="off"
        placeholder={placeholder}
        onChange={(e) => { setEditing(true); setText(e.target.value); }}
        onBlur={commit}
        onMouseDown={() => { if (!disabled) { setByKey(false); setOpen(true); } }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') { e.preventDefault(); setByKey(true); setOpen(true); return; }
          if (e.key === 'Escape' && open) { e.preventDefault(); e.stopPropagation(); setOpen(false); return; }
          if (e.key === 'Enter' && editing) {
            // Enter takes a typed date first; a second Enter reaches the page's own handler.
            e.preventDefault();
            commit();
            return;
          }
          onKeyDown?.(e);
        }}
        aria-haspopup="dialog"
        aria-expanded={open}
        {...aria}
      />
      <button type="button" className="date-btn" tabIndex={-1} disabled={disabled} aria-label="Choose a date from the calendar"
        onMouseDown={(e) => e.preventDefault()} onClick={() => { setOpen((o) => !o); input.current?.focus(); }}>
        <Icon name="calendar" size={16} />
      </button>
      {open && field.current && (
        <Calendar anchor={field.current} value={value} min={min} max={max} onPick={pick} focusOnOpen={byKey}
          onClose={() => { setOpen(false); input.current?.focus(); }} allowClear={value !== ''} onClear={() => pick('')} />
      )}
    </div>
  );
}

function Calendar({ anchor, value, min, max, onPick, onClose, allowClear, onClear, focusOnOpen }: {
  anchor: HTMLElement; value: string; min?: string | undefined; max?: string | undefined; focusOnOpen: boolean;
  onPick: (iso: string) => void; onClose: () => void; allowClear: boolean; onClear: () => void;
}) {
  const now = today();
  const [focus, setFocus] = useState(() => clamp(partsOf(value) ? value : now, min, max));
  const p = partsOf(focus)!;
  const pop = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  // Below the field, or above it when there is no room; kept inside the window.
  useLayoutEffect(() => {
    const place = () => {
      const r = anchor.getBoundingClientRect();
      const h = pop.current?.offsetHeight ?? 380;
      const w = pop.current?.offsetWidth ?? 320;
      const below = r.bottom + 6 + h <= window.innerHeight || r.top - 6 - h < 0;
      setPos({ top: below ? r.bottom + 6 : r.top - 6 - h, left: Math.max(8, Math.min(r.left, window.innerWidth - w - 8)) });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); };
  }, [anchor]);

  // Clicks outside close it.
  useEffect(() => {
    const down = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!pop.current?.contains(t) && !anchor.contains(t)) onClose();
    };
    document.addEventListener('mousedown', down);
    return () => document.removeEventListener('mousedown', down);
  }, [anchor, onClose]);

  // Keep keyboard focus on the focused day once the calendar is in use.
  const keyboard = useRef(focusOnOpen);
  useEffect(() => {
    if (keyboard.current) pop.current?.querySelector<HTMLButtonElement>(`[data-day="${focus}"]`)?.focus();
  }, [focus]);

  const move = (iso: string) => setFocus(clamp(iso, min, max));
  const onKey = (e: ReactKeyboardEvent) => {
    const map: Record<string, () => string> = {
      ArrowLeft: () => addDaysIso(focus, -1), ArrowRight: () => addDaysIso(focus, 1),
      ArrowUp: () => addDaysIso(focus, -7), ArrowDown: () => addDaysIso(focus, 7),
      PageUp: () => addMonthsIso(focus, e.shiftKey ? -12 : -1), PageDown: () => addMonthsIso(focus, e.shiftKey ? 12 : 1),
      Home: () => addDaysIso(focus, -new Date(`${focus}T00:00:00Z`).getUTCDay()),
      End: () => addDaysIso(focus, 6 - new Date(`${focus}T00:00:00Z`).getUTCDay()),
    };
    if (map[e.key]) { e.preventDefault(); keyboard.current = true; move(map[e.key]!()); return; }
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(); }
  };

  const years: number[] = [];
  const minY = partsOf(min ?? '')?.y ?? Math.min(p.y, Number(now.slice(0, 4))) - 10;
  const maxY = partsOf(max ?? '')?.y ?? Math.max(p.y, Number(now.slice(0, 4))) + 5;
  for (let y = minY; y <= maxY; y++) years.push(y);

  return createPortal(
    <div ref={pop} className="cal-pop" role="dialog" aria-label="Choose a date" onKeyDown={onKey}
      style={pos ? { top: pos.top, left: pos.left } : { visibility: 'hidden', top: 0, left: 0 }}>
      <div className="cal-head">
        <button type="button" className="cal-nav" onClick={() => move(addMonthsIso(focus, -1))} aria-label="Previous month"><Icon name="back" size={16} /></button>
        <select className="cal-sel" value={p.m} onChange={(e) => move(isoOf(p.y, Number(e.target.value), 1))} aria-label="Month">
          {MONTH_NAMES.map((name, i) => <option key={name} value={i}>{name}</option>)}
        </select>
        <select className="cal-sel" value={p.y} onChange={(e) => move(addMonthsIso(focus, (Number(e.target.value) - p.y) * 12))} aria-label="Year">
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <button type="button" className="cal-nav cal-next" onClick={() => move(addMonthsIso(focus, 1))} aria-label="Next month"><Icon name="back" size={16} /></button>
      </div>
      <div className="cal-grid" role="grid" aria-label={`${MONTH_NAMES[p.m]} ${p.y}`}>
        {WEEKDAYS.map((d) => <div key={d} className="cal-wd" role="columnheader" aria-hidden="true">{d}</div>)}
        {monthGrid(p.y, p.m).map((iso) => {
          const off = outOfRange(iso, min, max);
          const cls = ['cal-day', iso.slice(0, 7) !== focus.slice(0, 7) ? 'is-out' : '', iso === now ? 'is-today' : '', iso === value ? 'is-sel' : ''].join(' ');
          return (
            <button key={iso} type="button" data-day={iso} className={cls} disabled={off} tabIndex={iso === focus ? 0 : -1}
              aria-label={`${longDate(iso)}${iso === now ? ', today' : ''}`} aria-pressed={iso === value}
              onClick={() => onPick(iso)} onFocus={() => setFocus(iso)}>
              {Number(iso.slice(8))}
            </button>
          );
        })}
      </div>
      <div className="cal-foot">
        <button type="button" className="btn small ghost" disabled={outOfRange(now, min, max)} onClick={() => onPick(now)}>Today</button>
        <span className="spacer" />
        {allowClear && <button type="button" className="btn small ghost" onClick={onClear}>Clear</button>}
        <button type="button" className="btn small" onClick={onClose}>Close</button>
      </div>
    </div>,
    document.body,
  );
}
